import React, { useCallback, useContext, useEffect } from 'react';
import { navigateToUrl } from 'single-spa';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
import { useTheme } from '@nbx/frontend-helpers/subscribe';
import { Context } from 'state/store';
import { setOverlay, setPushNotification } from 'state/creators';
import OVERLAYS from 'constants/overlays';
import {
  PUSH_NOTIFICATIONS_SUBJECTS,
  PUSH_NOTIFICATIONS_SUBJECT,
  isCardAuthorization,
  isCardNotificationToRedirect,
  isReferral
} from 'constants/pushNotifications';
import usePushNotification from 'helpers/usePushNotification';
import { CardAuthorizationModal } from 'components/app/CardAuthorizationModal';

const log = (...args) => console.log('<PushNotificationsProvider>', ...args);

const skipBiometricRedirect = async () =>
  NBXStorage.setItem('skip-biometric-redirect', true, { useLocalStorage: true });

export const PushNotificationsProvider = () => {
  useEffect(() => {
    log('mount');
    return () => log('dismount');
  }, []);

  const [state, dispatch] = useContext(Context);
  const theme = useTheme();

  const handlePushNotification = useCallback(
    async notification => {
      dispatch(setPushNotification(notification));
      await skipBiometricRedirect();
      if (isCardAuthorization(notification)) {
        log('setOverlay(OVERLAYS.cardAuthorization)', notification);
        dispatch(setOverlay(OVERLAYS.cardAuthorization));
      } else if (isCardNotificationToRedirect(notification)) {
        navigateToUrl('/card');
      } else if (notification.subject === PUSH_NOTIFICATIONS_SUBJECT.DEPOSIT_RECEIVED) {
        navigateToUrl('/funds/deposits');
      } else if (notification.subject === PUSH_NOTIFICATIONS_SUBJECT.TRADE_MATCHED) {
        navigateToUrl(`/orders/${notification.order_id}`);
      } else if (notification.subject === PUSH_NOTIFICATIONS_SUBJECT.STAKING_REWARD_RECEIVED) {
        navigateToUrl('/staking');
      } else if (isReferral(notification)) {
        navigateToUrl('/referral');
      } else if (notification.subject === PUSH_NOTIFICATIONS_SUBJECT.WITHDRAWAL_SENT) {
        navigateToUrl(`/funds/withdrawals?asset=${notification.asset}`);
      } else if (notification.subject === PUSH_NOTIFICATIONS_SUBJECT.KYC_RENEWAL_REQUEST) {
        navigateToUrl(`/account`);
      } else if (notification.subject === PUSH_NOTIFICATIONS_SUBJECT.PRICE_VOLATILITY) {
        navigateToUrl(`/markets/${notification.market}`);
      } else if (notification.url) {
        navigateToUrl(notification.url);
      }
    },
    [dispatch]
  );

  const clearOverlay = useCallback(() => {
    log('clearOverlay');
    dispatch(setPushNotification(null));
    dispatch(setOverlay(null));
  }, [dispatch]);

  usePushNotification(PUSH_NOTIFICATIONS_SUBJECTS, handlePushNotification, {
    onlyOnTap: true
  });

  useEffect(() => {
    log('state.pushNotification', state.pushNotification);
  }, [state.pushNotification]);

  return (
    state.overlay === OVERLAYS.cardAuthorization &&
    state.pushNotification && (
      <nbx-theme-provider theme={theme}>
        <React.Suspense fallback={React.Fragment}>
          {state.pushNotification?.subject === PUSH_NOTIFICATIONS_SUBJECT.AUTH_3DS ? (
            <CardAuthorizationModal
              closeModal={clearOverlay}
              subject={state.pushNotification.subject}
              sessionId={state.pushNotification.sessionId}
              cardId={null}
            />
          ) : (
            <CardAuthorizationModal
              closeModal={clearOverlay}
              subject={state.pushNotification.subject}
              sessionId={state.pushNotification.id}
              cardId={state.pushNotification.cardId}
            />
          )}
        </React.Suspense>
      </nbx-theme-provider>
    )
  );
};
